import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { prefetchDashboardData } from '../utils/prefetchDashboard';

interface OAuthCallbackHandlerProps {
  provider: 'google' | 'github';
}

export function OAuthCallbackHandler({ provider }: OAuthCallbackHandlerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetch } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for error in URL params (from backend redirect)
      const errorParam = searchParams.get('error');
      if (errorParam === 'auth_failed') {
        setError('Authentication failed. Please try again.');
        return;
      }

      try {
        // OAuth callback sets cookies, so just refetch user data
        await refetch();
        prefetchDashboardData(queryClient);
        // Notify sibling same-origin tabs (BroadcastChannel works inside one
        // top-level partition; it does NOT cross the extension-iframe boundary).
        try {
          const bc = new BroadcastChannel('danny-auth');
          bc.postMessage({ type: 'login-complete' });
          bc.close();
        } catch {
          // BroadcastChannel not supported — not critical
        }
        // Popup flow (opened via window.open from an iframe like the extension
        // side panel): postMessage to the opener so the iframe can refetch,
        // then close this tab. window.opener is a live cross-context reference,
        // so it crosses the storage partition that BroadcastChannel can't.
        if (window.opener) {
          try {
            window.opener.postMessage(
              { type: 'login-complete' },
              window.location.origin,
            );
          } catch {
            // opener gone or cross-origin — best effort
          }
          window.close();
          return;
        }
        navigate('/app', { replace: true });
      } catch (err) {
        setError('Failed to complete authentication. Please try again.');
      }
    };

    handleCallback();
  }, [searchParams, refetch, navigate, queryClient]);

  if (error) {
    return (
      <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center pt-safe px-4">
        <div className="w-full max-w-md">
          <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-lg shadow-lg border border-border-subtle-light dark:border-border-subtle-dark p-8">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-950/30 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                Authentication Failed
              </h1>
              <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center pt-safe">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4" />
        <p className="text-text-secondary-light dark:text-text-secondary-dark">
          Completing {provider} authentication...
        </p>
      </div>
    </div>
  );
}
